import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import moment from "moment";
import { toast } from "react-toastify";
import swal from "sweetalert";
import AuthUser from "../../Hooks/authUser";
import CreateUserHook from "../../Hooks/createUserHook";
import logo from "../../Assets/Logo(1).svg";

const LoginSignUp = () => {
  const { http, setToken } = AuthUser();
  const navigate = useNavigate();
  const location = useLocation();
  const issueDate = moment().format("YYYY-MM-DD");

  // Determine initial tab based on route
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (location.pathname === "/signUp") {
      setActiveTab("signup");
    } else {
      setActiveTab("login");
    }
  }, [location.pathname]);

  // Function to check if the user has an active package
  const getPackage = async (user, id) => {
    try {
      const response = await fetch(
        `https://servercareseebd.careseebd.com/api/v1/package/specific?fieldName=userId&&fieldValue=${id}`
      );
      const data = await response.json();
      if (data?.data?.length) {
        const isExpire = data?.data?.[0]?.expireDate
          ? moment(issueDate).isAfter(data?.data?.[0]?.expireDate)
          : true;

        if (!isExpire) {
          navigate(`/${user?.role}Dashboard`);
          window.location.reload();
        } else {
          toast.error("You don't have any active package! :(");
          navigate(`/`);
          window.location.reload();
        }
      } else {
        // No package found
        toast.warning("No active therapy plan found. Please select a plan.");
        navigate("/pricing");
      }
    } catch (error) {
      console.error("Error fetching package:", error);
    } finally {
      setLoading(false);
    }
  };

  // POST login, retrying when no response reaches the browser. On the shared host,
  // a request over the concurrent-process limit gets a LiteSpeed error page with
  // no CORS headers, which the browser reports as a network/CORS error.
  const postLogin = async (payload, attempt = 1) => {
    try {
      return await http.post("/user/login", payload);
    } catch (err) {
      if (!err.response && attempt < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
        return postLogin(payload, attempt + 1);
      }
      throw err;
    }
  };

  // Handle Login Submit
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const email = e.target.email.value?.trim();
    const password = e.target.password.value;

    try {
      const res = await postLogin({ email, password });
      if (res?.data?.status === "success") {
        localStorage.setItem("careSeeAccessToken", res?.data?.token);
        toast.success("Login Successful");

        setToken(
          res.data.data.user.email,
          res.data.data.token,
          res.data.data.user.role,
          res.data.data.user,
          res.data.data.userIp
        );

        if (res.data?.data?.data?.role === "user") {
          const d = res?.data?.data?.data;
          await getPackage(d, d?._id);
        } else {
          navigate(`/${res.data?.data?.data?.role}Dashboard`);
          window.location.reload();
        }
      } else {
        toast.error("Login failed. Check your credentials.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Login error", err);
      
      let errorMessage = "Invalid email or password.";
      if (!err.response || err.code === "ERR_NETWORK") {
        errorMessage = "Could not reach the server. Please check your connection and try again in a few seconds.";
      } else if (err.response?.data?.message || err.response?.data?.error) {
        errorMessage = err.response.data.message || err.response.data.error;
      }

      swal("Login Failed", errorMessage, "error");
      setLoading(false);
    }
  };

  // Handle Signup Submit
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const name = e.target.name.value;
    const email = e.target.email.value;
    const phone = e.target.phone.value;
    const dateOfBirth = e.target.dateOfBirth.value;
    const password = e.target.password.value;
    const confirmPassword = e.target.confirmPassword.value;

    if (password !== confirmPassword) {
      toast.error("Passwords do not match!");
      setLoading(false);
      return;
    }

    const user = { name, email, phone, password, confirmPassword, dateOfBirth };

    try {
      await CreateUserHook(user, navigate);
      setActiveTab("login");
    } catch (error) {
      console.error("Signup error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative Background Patterns */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.05),transparent)] pointer-events-none" />

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 text-center mb-6">
        <Link to="/" className="inline-block">
          <img className="mx-auto h-10 w-auto" src={logo} alt="Medmyne Logo" />
        </Link>
      </div>

      <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-5xl relative z-10 px-4">
        <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-gray-200/50 overflow-hidden grid grid-cols-1 lg:grid-cols-12">

          {/* Left Column: Branding / Marketing Message */}
          <div className="lg:col-span-5 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 p-8 sm:p-12 text-white flex flex-col justify-between relative overflow-hidden">
            {/* Visual patterns inside left panel */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent)] pointer-events-none" />

            <div className="relative z-10">
              <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-md rounded-full px-3.5 py-1 text-[10px] font-bold uppercase tracking-wider mb-6 border border-white/10">
                <Icon icon="solar:shield-check-bold" className="w-3.5 h-3.5 text-emerald-400" />
                Clinical Grade Security
              </span>
              <h2 className="font-heading text-3xl font-extrabold leading-tight mb-4">
                Empowering Vision Restoration
              </h2>
              <p className="text-sm text-indigo-100/90 leading-relaxed">
                Log in to access your customized scientific vision exercises, monitor therapy benchmarks, and track eye recovery metrics.
              </p>
            </div>

            <div className="relative z-10 mt-12 lg:mt-0 pt-8 border-t border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/10">
                  <Icon icon="solar:heart-bold" className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-indigo-200 uppercase tracking-wider">Trusted by Specialists</p>
                  <p className="text-sm font-bold">Over 10,000+ Active Patients</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Dynamic Form (Login/Signup switcher) */}
          <div className="lg:col-span-7 p-8 sm:p-12">

            {/* Custom Tab Toggles */}
            <div className="flex justify-center mb-8">
              <div className="bg-slate-100/80 p-1 rounded-2xl inline-flex gap-1 border border-slate-200/50">
                <button
                  onClick={() => setActiveTab("login")}
                  className={`px-6 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === "login"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                    }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setActiveTab("signup")}
                  className={`px-6 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === "signup"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                    }`}
                >
                  Sign Up
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Processing request...</p>
              </div>
            ) : activeTab === "login" ? (
              /* LOGIN FORM */
              <form onSubmit={handleLoginSubmit} className="space-y-5">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                      <Icon icon="solar:envelope-linear" className="w-4 h-4" />
                    </span>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="e.g. name@domain.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      Password
                    </label>
                    <Link
                      to="/resetPassword"
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-700 uppercase tracking-wider"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                      <Icon icon="solar:lock-linear" className="w-4 h-4" />
                    </span>
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-450 hover:text-gray-700"
                    >
                      <Icon icon={showPassword ? "solar:eye-closed-bold" : "solar:eye-bold"} className="w-4.5 h-4.5 text-gray-400" />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-indigo-600/10 mt-6"
                >
                  <Icon icon="solar:login-2-bold" className="w-4 h-4" />
                  Sign In
                </button>
              </form>
            ) : (
              /* SIGNUP FORM */
              <form onSubmit={handleSignupSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Full Name
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <Icon icon="solar:user-linear" className="w-4 h-4" />
                      </span>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="e.g. Ashik Ahmed"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>

                  {/* DOB */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Date of Birth
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <Icon icon="solar:calendar-linear" className="w-4 h-4" />
                      </span>
                      <input
                        name="dateOfBirth"
                        type="date"
                        required
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Email */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <Icon icon="solar:envelope-linear" className="w-4 h-4" />
                      </span>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="e.g. name@domain.com"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Phone Number
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <Icon icon="solar:phone-linear" className="w-4 h-4" />
                      </span>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="e.g. +8801700000000"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Password */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <Icon icon="solar:lock-linear" className="w-4 h-4" />
                      </span>
                      <input
                        name="password"
                        type="password"
                        required
                        placeholder="••••••••"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <Icon icon="solar:lock-linear" className="w-4 h-4" />
                      </span>
                      <input
                        name="confirmPassword"
                        type="password"
                        required
                        placeholder="••••••••"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 text-sm font-semibold text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-indigo-600/10 mt-6"
                >
                  <Icon icon="solar:user-plus-bold" className="w-4 h-4" />
                  Sign Up
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSignUp;
